import { useEffect, useState } from "react";
import { getBrowserClient } from "../../supabase/browser.ts";

interface CurrentSlate {
  tournament: string | null;
  fixtures: number;
  priced: number;
  nextKickoff: string | null;
  loading: boolean;
}

export function useCurrentInternationalSlate(): CurrentSlate {
  const [slate, setSlate] = useState<CurrentSlate>({
    tournament: null, fixtures: 0, priced: 0, nextKickoff: null, loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // The generated DB type has not yet incorporated the international tables.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const client = getBrowserClient() as any;
        const tournaments = await client.from("intl_acca_tournaments")
          .select("id,name").eq("status", "ACTIVE")
          .order("start_date", { ascending: false }).limit(1);
        if (tournaments.error) throw tournaments.error;
        const current = tournaments.data?.[0] as { id: string; name: string } | undefined;
        if (!current) {
          if (!cancelled) setSlate({ tournament: null, fixtures: 0, priced: 0, nextKickoff: null, loading: false });
          return;
        }
        const now = new Date();
        const horizon = new Date(now.getTime() + 7 * 86_400_000);
        const matches = await client.from("intl_acca_matches")
          .select("kickoff,market_odds")
          .eq("tournament_id", current.id).eq("status", "upcoming")
          .gte("kickoff", now.toISOString()).lt("kickoff", horizon.toISOString())
          .order("kickoff", { ascending: true });
        if (matches.error) throw matches.error;
        const rows = (matches.data ?? []) as Array<{ kickoff: string; market_odds: Record<string, number> | null }>;
        if (!cancelled) setSlate({
          tournament: current.name,
          fixtures: rows.length,
          priced: rows.filter((row) => row.market_odds && Object.keys(row.market_odds).length > 0).length,
          nextKickoff: rows[0]?.kickoff ?? null,
          loading: false,
        });
      } catch {
        if (!cancelled) setSlate({ tournament: null, fixtures: 0, priced: 0, nextKickoff: null, loading: false });
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  return slate;
}
